import React from "react";
import { Typography, Link, Box, Tab, Stack, OutlinedInput, InputAdornment, Avatar } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../Router/router";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabs from "../PanelTabs";
import PanelAccordion from "../PanelAccordion/panel-accordion";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import ContractDetailsCard from "../../Cards/ContractDetailsCard/contract-details-card";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { cacheService, orderService, rightService, userService } from "../../../Core/Api/services";
import moment from "moment";
import { RightType } from "../../../Core/Api/Types/right-type";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import TableCellName from "../../Table/TableCellName";
import { UserAdvancedInfos } from "../../../Core/Api/Types/user-advanced-infos";
import TableCellBool from "../../Table/TableCellBool";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export interface CustomerDetailsPanelProps {
  width?: number;
  open: boolean;
  customer: DbCustomerInfos;
  onClose?: React.MouseEventHandler;
}

const CustomerDetailsPanel: React.FC<CustomerDetailsPanelProps> = (props) => {
  const { t } = useTranslation();
  const { customer } = props;
  const [value, setValue] = React.useState(0);
  const [data, setData] = React.useState<any[]>([]);
  const [costumerOrderItems, setCostumerOrderItems] = React.useState<DbOrderItemInfos[]>([]);
  const [costumerLinkedUsers, setCostumerLinkedUsers] = React.useState<UserAdvancedInfos[]>([]);
  const [contractSearch, setContractSearch] = React.useState<string>("");
  const [userSearch, setUserSearch] = React.useState<string>("");
  const metadataFields = React.useMemo(() => cacheService.GetMetadata(), []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // RightContracts
  React.useEffect(() => {
    if (customer?.id) {
      rightService.GetRightContractsByCustomerId(customer.id, metadataFields, true).then((response) => {
        setData(response);
      });
    }
  }, [customer]);

  // Orders
  React.useEffect(() => {
    if (customer?.id) {
      orderService.GetOrdersByCustomerId(customer.id).then((response) => {
        if (response.items) setCostumerOrderItems(response.items);
      });
    }
  }, [customer]);

  // Users
  React.useEffect(() => {
    if (customer?.id) {
      userService.GetUsersListByCustomerId(customer.id).then((response) => {
        if (response.users) {
          setCostumerLinkedUsers(response.users);
        }
      });
    }
  }, [customer]);

  if (customer) {
    return (
      <Panel
        header={
          <PanelHeader
            title={t("panel:title.customerDetails")}
            onClose={props.onClose as PanelHeaderProps["onClose"]}
            rightContent={
              <Link
                component={RouterLink}
                to={`${routePaths.customers.path}/${customer.id}`}
                underline="hover"
                color="primary.text"
              >
                <OpenInFullOutlinedIcon sx={{ mr: 0.5 }} /> {t("common:open")}
              </Link>
            }
          />
        }
        reload={customer.id}
        open={props.open}
        onClose={props.onClose}
      >
        <Stack direction="row" justifyContent="space-between" pb={2} spacing={3}>
          <Stack flex={1}>
            <Typography variant="h6" color="text.placeholder">
              {t("common:customerName")}
            </Typography>
            <Typography variant="h3" component="h2">
              {customer.name}
            </Typography>
          </Stack>
          <Avatar src="https://picsum.photos/64/64" sx={{ width: 60, height: 60, flexShrink: 0 }} />
        </Stack>
        <PanelTabs value={value} onChange={handleChange} aria-label="basic tabs example" variant="fullWidth">
          <Tab label={t("common:details")} {...a11yProps(0)} />
          <Tab label={t("common:contractsAndRights")} {...a11yProps(1)} />
          <Tab label={t("common:users")} {...a11yProps(2)} />
        </PanelTabs>
        <Box mt={2}>
          <PanelTabContent id="customer-details-panel" activePanelIndex={value} index={0}>
            <PanelAccordion title={t("panel:accordionTitle.globalInfos")}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("menu:title.fullName")}>{customer.name}</MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.databaseId")}>{customer.id}</MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.clientExternalId")}>
                  {customer.customerCodeId}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:enability")}>
                  <TableCellBool value={!customer.addedManually} />
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.addedManually")}>
                  <TableCellBool value={!customer.disabled} />
                </MetadataListTextItem>
              </Stack>
            </PanelAccordion>
            <PanelAccordion title={t("panel:accordionTitle.contractsAndRightsInfos")}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("menu:title.numOfContracts")}>{data.length}</MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.disposalContracts")}>
                  {data.filter((contract) => contract.type === RightType.Disposal).length}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.acquisitionContracts")}>
                  {data.filter((contract) => contract.type === RightType.Acquisition).length}
                </MetadataListTextItem>
              </Stack>
            </PanelAccordion>
            <PanelAccordion title={t("panel:accordionTitle.ordersInfos")}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("menu:title.numbersOfOrders")}>
                  {costumerOrderItems.length}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.completedOrders")}>
                  {costumerOrderItems.filter((or) => or.status === OrderStatus.COMPLETED).length}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.rejectedOrders")}>
                  {
                    costumerOrderItems.filter(
                      (or) =>
                        or.status === OrderStatus.CANCELED ||
                        or.status === OrderStatus.FAILED ||
                        or.status === OrderStatus.ABORTED
                    ).length
                  }
                </MetadataListTextItem>
              </Stack>
            </PanelAccordion>
          </PanelTabContent>

          <PanelTabContent id="customer-details-panel" activePanelIndex={value} index={1}>
            <OutlinedInput
              label={t("common:search")}
              sx={{ width: "100%" }}
              placeholder={t("common:search")}
              onChange={(e) => setContractSearch(e.currentTarget.value)}
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              }
            />

            {data &&
              data.length > 0 &&
              data
                .filter((x) => x.titleInfos.name?.toLowerCase().includes(contractSearch.toLowerCase()))
                .map((el, index) => (
                  <Stack spacing={1} mt={2} key={index}>
                    <ContractDetailsCard
                      contractId={el.titleId}
                      contractTitle={el.titleInfos.name}
                      customerName={el.customerName}
                      broadcaster={el.broadcasterName}
                      startDate={moment(el.startDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                      endDate={moment(el.endDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                      id={el.externalId}
                      workId={el.workRecordId}
                      externalId={el.externalId}
                      rightType={el.type}
                      languages={el.rightLanguagesString.trim().split(" ")}
                      territories={el.rightTerritoriesString.trim().split(" ")}
                      mediaName={el.mediaNamesString}
                      titleType={el.titleType}
                      okDelivery={el.okDelivery}
                      small
                    />
                  </Stack>
                ))}
            {data === undefined ||
              (data.length < 1 && (
                <Typography color="text.tier" mt={2}>
                  {t("panel:customerPanel.body.NoContractsAndRightsAvailable")}
                </Typography>
              ))}
          </PanelTabContent>

          <PanelTabContent id="customer-details-panel" activePanelIndex={value} index={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={2}>
              <OutlinedInput
                label={t("common:search")}
                sx={{ width: "100%" }}
                placeholder={t("common:search")}
                onChange={(e) => setUserSearch(e.currentTarget.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
            </Stack>
            <Stack spacing={1.5} mb={2}>
              {costumerLinkedUsers
                .filter((user) => user.userInfos?.name?.toLowerCase().includes(userSearch))
                .map((user) => (
                  <TableCellName
                    key={user.userInfos?.id}
                    data={{
                      value: {
                        firstname: user.userInfos?.firstName || "-",
                        lastname: user.userInfos?.lastName || "-",
                        id: user.userInfos!.id,
                      },
                    }}
                  />
                ))}
              {costumerLinkedUsers === undefined ||
                (costumerLinkedUsers.length < 1 && (
                  <Typography color="text.tier">{t("panel:customerPanel.body.noUsersLinkedToCustomer")}</Typography>
                ))}
            </Stack>
          </PanelTabContent>
        </Box>
      </Panel>
    );
  } else {
    return <span></span>;
  }
};

function a11yProps(index: number) {
  return {
    id: `customer-details-panel-tab-${index}`,
    "aria-controls": `customer-details-panel-tabpanel-${index}`,
  };
}

export default CustomerDetailsPanel;
